import { useAnimation } from '../UNSAFE_useAnimation';
import { useId } from '../UNSAFE_useId';
import { cssProps } from '@oracle/oraclejet-cssprops';
import { classNames } from '../../utils/UNSAFE_classNames';
import { useEffect, useRef } from 'preact/hooks';
import { HTMLAttributesSignalExcluded } from '../../utils/UNSAFE_attributeUtils';

type IntrinsicProps = Pick<HTMLAttributesSignalExcluded<HTMLDivElement>, 'children'>;

type Status = 'mounted' | 'unmounted';

type ContentProps = IntrinsicProps & {
  isOpen: boolean;
  isDatatip: boolean;
  onTransitionEnd: () => void;
};

const styles = {
  wrapper: cssProps`${{
    display: 'flex',
    alignItems: 'flex-end',
    overflowY: 'hidden'
  }}`,
  inner: cssProps`${{
    opacity: '0',
    maxHeight: '0'
  }}`,
  base: cssProps`${{
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-tooltip-text-color)',
    backgroundColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-tooltip-bg-color)',
    whiteSpace: 'nowrap',
    paddingTop: 'var(--oj-c-PRIVATE-DO-NOT-USE-tooltip-padding-vertical)',
    paddingLeft: 'var(--oj-c-PRIVATE-DO-NOT-USE-tooltip-padding-horizontal)',
    paddingRight: 'var(--oj-c-PRIVATE-DO-NOT-USE-tooltip-padding-horizontal)',
    paddingBottom: 'var(--oj-c-PRIVATE-DO-NOT-USE-tooltip-padding-vertical)',
    borderRadius: 'var(--oj-c-PRIVATE-DO-NOT-USE-tooltip-border-radius)',
    borderColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-tooltip-border-color)',
    fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-tooltip-font-size)',
    borderWidth: 'var(--oj-c-PRIVATE-DO-NOT-USE-tooltip-border-width)',
    borderStyle: 'solid'
  }}`,
  datatip: cssProps`${{
    '--oj-c-PRIVATE-DO-NOT-USE-tooltip-bg-color':
      'rgb(var(--oj-c-PRIVATE-DO-NOT-USE-palette-neutral-rgb-0))',
    '--oj-c-PRIVATE-DO-NOT-USE-tooltip-text-color':
      'rgb(var(--oj-c-PRIVATE-DO-NOT-USE-palette-neutral-rgb-190))',
    '--oj-c-PRIVATE-DO-NOT-USE-tooltip-padding-vertical':
      'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-2x)',
    '--oj-c-PRIVATE-DO-NOT-USE-tooltip-border-radius':
      'var(--oj-c-PRIVATE-DO-NOT-USE-core-border-radius-md)',
    '--oj-c-PRIVATE-DO-NOT-USE-tooltip-border-color':
      'rgba(var(--oj-c-PRIVATE-DO-NOT-USE-palette-neutral-rgb-190), 0.65)',
    '--oj-c-PRIVATE-DO-NOT-USE-tooltip-font-size':
      'var(--oj-c-PRIVATE-DO-NOT-USE-typography-body-xs-font-size)'
  }}`
};

export const TooltipContent = ({
  children,
  isOpen,
  isDatatip,
  onTransitionEnd,
  ...props
}: ContentProps) => {
  const uniqueID = useId();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const { nodeRef } = useAnimation<Status, HTMLDivElement>(isOpen ? 'mounted' : 'unmounted', {
    animationStates: {
      mounted: (_node) => {
        const childNode = _node.firstChild as HTMLDivElement;
        return {
          from: {
            maxHeight: '0',
            opacity: '0%'
          },
          to: {
            maxHeight: `${childNode?.offsetHeight}px`,
            opacity: isDatatip ? '100%' : '95%'
          },
          options: {
            duration: isDatatip ? 1 : 100,
            easing: [0.0, 0.0, 0.2, 1]
          }
        };
      },
      unmounted: (_node) => {
        return {
          from: {
            opacity: isDatatip ? '100%' : '95%'
          },
          to: {
            maxHeight: '0',
            opacity: '0%'
          },
          options: {
            duration: isDatatip ? 1 : 100,
            easing: [0.0, 0.0, 0.2, 1]
          }
        };
      }
    },
    isAnimatedOnMount: true,
    onAnimationEnd: () => onTransitionEnd?.()
  });

  // Set up initial wrapper height so Tooltip can animate from the bottom to the top
  useEffect(() => {
    if (wrapperRef.current && popoverRef.current?.offsetHeight) {
      wrapperRef.current.style.height = `${popoverRef.current?.offsetHeight}px`;
    }
  }, []);

  return (
    <div ref={wrapperRef} id={uniqueID} role="tooltip" class={styles.wrapper}>
      <div ref={nodeRef} class={classNames([styles.inner])} {...props}>
        <div ref={popoverRef} class={classNames([styles.base, isDatatip ? styles.datatip : ''])}>
          {children}
        </div>
      </div>
    </div>
  );
};
